import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { parseBannerList } from 'helpers/parseBannerList';

import { Slider } from '@atlas/components';
import {
  AccountManager,
  btag,
  pushToDataLayer,
  useApplicationState,
  useBannerGroupList,
  useCasinoNewState,
} from '@atlas/core';

import { filterBannersList, isUrlAbsolute } from 'common/utils';
import { useIsRtl } from 'hooks/useIsRtl';
import history from 'components/Layout/history';
import { GROUPS } from 'src/components/Casino/constants';

export const CasinoBanner = () => {
  const { authenticated } = useApplicationState();
  const { group } = useCasinoNewState();
  const { bannersChunk } = useBannerGroupList(
    'casino',
    'desktop',
    group === 'LIVE' && 'live_desktop',
  );
  const isRtl = useIsRtl();

  const groups = useMemo(() => bannersChunk.map(parseBannerList), [bannersChunk]);
  const dir = useMemo(() => (isRtl ? 'rtl' : 'ltr'), [isRtl]);

  const filteredGroups = useMemo(
    () =>
      groups
        .map(group => filterBannersList(group, authenticated))
        .filter(group => group.length > 0),
    [authenticated, groups],
  );

  const handleClick = useCallback(
    (event, { href, imageUrl, target }) => {
      const { clientId } = AccountManager.account;

      pushToDataLayer(
        group === GROUPS.CASINO ? 'сasino_banner_click' : 'live_сasino_banner_click',
        { client: { id: clientId } },
        {
          image_URL: imageUrl,
        },
        btag,
      );

      const isInternalLink =
        href && !isUrlAbsolute(href) && !href.startsWith('//') && target !== '_blank';

      if (isInternalLink) {
        event?.preventDefault();
        history.push(href.startsWith('/') ? href : `/${href}`);
      }
    },
    [group],
  );

  if (!filteredGroups.length) return null;

  return (
    <Slider
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      className={cn('slider', { 'slider--single-slide': filteredGroups.length === 1 })}
      dir={dir}
      isNewDesign
      loop={filteredGroups.length > 1}
      nextBtn={<Slider.NextBtn />}
      prevBtn={<Slider.PrevBtn />}
      slidesPerView={1}
    >
      {filteredGroups.map(group => (
        <Slider.Slide key={group.reduce((acc, next) => `${acc}${next.imageUrl}`, '')}>
          <Slider.Group>
            {group.map(({ href, imageUrl, target, title }) => (
              <Slider.Banner
                href={href}
                imageUrl={imageUrl}
                key={imageUrl}
                onClick={event => handleClick(event, { href, imageUrl, target })}
                target={target}
                title={title}
              />
            ))}
          </Slider.Group>
        </Slider.Slide>
      ))}
    </Slider>
  );
};
